import { View, Text } from "../base";
import { Icon } from "../base/Icon";
import { JsonViewer } from "../base/JsonViewer";
import { SpanEvent } from "../../redux/reducers/sessionTraceSlice";

/**
 * Renders OTLP span events as color-coded cards. Used by every span detail
 * view that has an Events tab. The proxy emits completion.attempt events
 * for each attempt in a fallback chain — this is the canonical place where
 * those (and any future event types) get rendered.
 */
export const SpanEventsContent = ({ events }: { events: SpanEvent[] }) => {
  if (events.length === 0) {
    return <Text className="text-sm text-gray-500 dark:text-gray-400">No events.</Text>;
  }
  return (
    <View className="space-y-3">
      {events.map((evt, i) => {
        // Generic success marker — any event type can opt in by stamping
        // event.success on its attributes. Without it (third-party events,
        // tracing without status), neither badge renders.
        const success = evt.attributes["event.success"];
        const succeeded = success === true;
        const failed = success === false;
        const ts = (() => {
          try {
            return new Date(evt.time).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              fractionalSecondDigits: 3,
            });
          } catch {
            return evt.time;
          }
        })();
        return (
          <View
            key={i}
            className={`rounded-lg border p-3 ${
              failed
                ? "bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800"
                : succeeded
                  ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800"
                  : "bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700"
            }`}
          >
            <View className="flex flex-row items-center justify-between mb-2">
              <View className="flex flex-row items-center gap-2">
                <Icon
                  name="zap"
                  size={14}
                  className="text-gray-500 dark:text-gray-400"
                />
                <Text className="text-sm font-medium text-gray-800 dark:text-gray-200">{evt.name}</Text>
                {failed && <Text className="text-xs text-red-600 dark:text-red-400 font-medium">FAILED</Text>}
                {succeeded && <Text className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">SUCCESS</Text>}
              </View>
              <Text className="text-xs font-mono text-gray-500 dark:text-gray-400">{ts}</Text>
            </View>
            {Object.keys(evt.attributes).length > 0 && (
              <View className="bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 overflow-hidden">
                <JsonViewer
                  data={evt.attributes}
                  collapsed={10}
                  className="p-2"
                />
              </View>
            )}
          </View>
        );
      })}
    </View>
  );
};
